import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { GroupOption, Option, OptionOriginal } from "PFTypes/options";
import { FocusEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUnmount } from "react-use";

import { InputFieldSet } from "../../text/input_field_set";
import { SelectV2Props } from "../select_v2.types";
import { getHighlightedOptionId } from "../select_v2.utils";
import { CreateButton } from "./create_button";
import css from "./dropdown.module.scss";
import { List, ListGrouped } from "./list";
import { NoItems } from "./no_items";
import { ResultOption } from "./types";
import { useDebouncedSearchTerm } from "./use_debounced_search_term";
import { DropdownData } from "./use_dropdown";

type DropdownProps<T> = {
  dropdownId: string;
  onBlur: (event: FocusEvent<HTMLDivElement>) => void;
  dropdownData: DropdownData<T>;
  style?: React.CSSProperties;
  searchInputQaId?: string;
  focusSearchInputOnOpen?: boolean;
} & Pick<
  SelectV2Props<T>,
  "value" | "multiple" | "clipOptionsOverflow" | "createButton" | "noOptionsText" | "dropdownHeaderElement"
>;

/*
 * Recommended use with useDropdown hook
 */
export const Dropdown = <T extends OptionOriginal = OptionOriginal>({
  dropdownId,
  value,
  multiple,
  onBlur,
  dropdownData,
  clipOptionsOverflow,
  createButton,
  noOptionsText,
  dropdownHeaderElement,
  style,
  searchInputQaId,
  focusSearchInputOnOpen = true
}: DropdownProps<T>) => {
  const {
    resultOptions,
    isMoreOptions,
    selectedOptionsLimit,
    isSelectedLimitReached,
    isFetchingAvailableOptions,
    isFetchingOptionsEnabled,
    isSearchable,
    searchTerm,
    setSearchTerm,
    highlightedIndex,
    setHighlightedIndex,
    handleKeyDown,
    handleCreateOption,
    areOptionsGrouped,
    handleChange
  } = dropdownData;

  const { t } = useTranslation("core");

  const { currentSearchTerm, setDebouncedSearchTerm } = useDebouncedSearchTerm({
    searchTerm,
    setSearchTerm,
    isFetchingOptionsEnabled
  });

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const highlightOption = (option: Option<T>, options: Option<T>[]) => {
    const optionIndex = options.findIndex(({ id }) => id === option.id);
    // highlights the option only in case some other option is already highlighted
    if (optionIndex > -1 && !isNil(highlightedIndex)) {
      setHighlightedIndex(optionIndex);
    }
  };

  useEffect(() => {
    if (focusSearchInputOnOpen) {
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [focusSearchInputOnOpen]);

  useUnmount(() => {
    setHighlightedIndex(null);
    setSearchTerm("");
  });

  const placeholder = isFetchingAvailableOptions
    ? undefined
    : isMoreOptions
      ? t("components.selectV2.searchMorePlaceholder")
      : t("components.selectV2.searchPlaceholer");

  return (
    <Stack className={css.root} style={style} gap={null}>
      <Stack className={css.content}>
        {isSearchable && (
          <InputFieldSet
            qaId={searchInputQaId}
            ref={searchInputRef}
            value={currentSearchTerm}
            onChange={setDebouncedSearchTerm}
            icon="search"
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-controls={dropdownId}
            aria-owns={dropdownId}
            aria-activedescendant={getHighlightedOptionId(highlightedIndex, resultOptions, dropdownId)}
            aria-autocomplete="list"
            placeholder={placeholder}
          />
        )}
        {dropdownHeaderElement?.(resultOptions)}
        {isEmpty(resultOptions) && !isFetchingAvailableOptions && (
          <NoItems searchTerm={searchTerm} noOptionsText={noOptionsText} />
        )}
        {isFetchingAvailableOptions && <LoadingDots />}
        {isSelectedLimitReached && (
          <Typography variant="labelRegular" className={css.maxReached}>
            {t("components.selectV2.maxSelectedReached", { count: selectedOptionsLimit })}
          </Typography>
        )}
        {areOptionsGrouped ? (
          <ListGrouped
            id={dropdownId}
            value={value}
            optionsGroups={resultOptions as GroupOption<T>[]}
            multiple={multiple}
            onChange={handleChange}
            highlightedIndex={highlightedIndex}
            highlightOption={highlightOption}
          />
        ) : (
          <List
            id={dropdownId}
            value={value}
            options={resultOptions as ResultOption<T>[]}
            multiple={multiple}
            onChange={handleChange}
            highlightedIndex={highlightedIndex}
            highlightOption={highlightOption}
            clipOptionsOverflow={clipOptionsOverflow}
          />
        )}
      </Stack>
      {!!handleCreateOption && (
        <>
          <Divider />
          <CreateButton
            onClick={handleCreateOption}
            onBlur={onBlur}
            label={createButton?.label ?? t("components.selectV2.create", { value: searchTerm })}
            tooltipContent={createButton?.tooltipContent}
          />
        </>
      )}
    </Stack>
  );
};
